import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { db, functions } from '../firebase';
import { collection, query, where, onSnapshot, doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { theme, responsiveContainer, createGradient } from '../styles/theme';
import AgentList from './AgentList';
import ByteBotLogo from './ByteBotLogo';

// Lazy load heavy components
const AgentSetup = lazy(() => import('./AgentSetup'));
const AgentEditor = lazy(() => import('./AgentEditor'));
const KnowledgeManager = lazy(() => import('./KnowledgeManager'));
const ConversationHistory = lazy(() => import('./ConversationHistory'));
const SubscriptionManager = lazy(() => import('./SubscriptionManager'));
const AccountDetails = lazy(() => import('./AccountDetails'));

// Loading Skeleton Component
function LoadingSkeleton() {
  return (
    <div style={{
      padding: theme.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.lg,
      fontFamily: theme.typography.fontFamily
    }}>
      <div style={{
        height: '40px',
        backgroundColor: '#f0f0f0',
        borderRadius: theme.borderRadius.md,
        animation: 'pulse 1.5s ease-in-out infinite'
      }} />
      <div style={{
        height: '200px',
        backgroundColor: '#f0f0f0',
        borderRadius: theme.borderRadius.md,
        animation: 'pulse 1.5s ease-in-out infinite'
      }} />
      <div style={{
        height: '100px',
        backgroundColor: '#f0f0f0',
        borderRadius: theme.borderRadius.md,
        animation: 'pulse 1.5s ease-in-out infinite'
      }} />
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}

// Dashboard Home Component
function DashboardHome({ user, agents, usageStats, userProfile, onEditAgent }) {
  const navigate = useNavigate();

  const handleCreateAgent = () => {
    navigate('/dashboard/setup');
  };

  const handleViewAccount = () => {
    navigate('/dashboard/account');
  };

  // Calculate billing cycle dates
  const getBillingCycleInfo = () => {
    if (!userProfile) return { cyclePeriod: 'Current cycle', daysRemaining: 0 };

    // Use billingCycleStart if available, otherwise use createdAt
    const cycleStart = userProfile.billingCycleStart?.toDate() || userProfile.createdAt?.toDate() || new Date();
    const nextReset = userProfile.nextResetDate?.toDate();
    
    if (nextReset) {
      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((nextReset - now) / (1000 * 60 * 60 * 24)));
      const cycleEndMonth = nextReset.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const cycleStartMonth = cycleStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      return {
        cyclePeriod: `${cycleStartMonth} - ${cycleEndMonth}`,
        daysRemaining
      };
    } else {
      // Fallback to monthly calculation from createdAt
      const now = new Date();
      const currentMonth = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + Math.floor((now - cycleStart) / (1000 * 60 * 60 * 24 * 30)), cycleStart.getDate());
      const nextMonth = new Date(currentMonth);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      const daysRemaining = Math.max(0, Math.ceil((nextMonth - now) / (1000 * 60 * 60 * 24)));
      const cycleEndMonth = nextMonth.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const cycleStartMonth = currentMonth.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      return {
        cyclePeriod: `${cycleStartMonth} - ${cycleEndMonth}`,
        daysRemaining
      };
    }
  };

  const billingInfo = getBillingCycleInfo();
  const isNewUser = agents.length === 0;
  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';

  const styles = {
    welcome: {
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
      padding: theme.spacing.xl,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.borderDark}`,
      boxShadow: theme.shadows.sm,
    },
    welcomeTitle: {
      fontSize: theme.typography.fontSizes['3xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    welcomeSubtitle: {
      fontSize: theme.typography.fontSizes.lg,
      color: theme.colors.textLight,
      lineHeight: '1.6',
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    newUserActions: {
      marginTop: theme.spacing.lg,
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    actionButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textDecoration: 'none',
      display: 'inline-block',
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textDecoration: 'none',
      display: 'inline-block',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing.xl,
      marginBottom: theme.spacing['3xl'],
    },
    statCard: {
      backgroundColor: theme.colors.surface,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    statValue: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.sm,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    statLabel: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.text,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: theme.typography.fontWeights.semibold,
      marginBottom: theme.spacing.xs,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    statSubtext: {
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.textMuted,
      lineHeight: '1.4',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
  };

  const handleStatCardHover = (e, isHovering) => {
    if (isHovering) {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = theme.shadows.lg;
    } else {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }
  };

  const handleButtonHover = (e, isHovering, isPrimary = true) => {
    if (isHovering) {
      if (isPrimary) {
        e.target.style.backgroundColor = theme.colors.primaryDark;
      } else {
        e.target.style.backgroundColor = theme.colors.primary;
        e.target.style.color = '#fff';
      }
      e.target.style.transform = 'translateY(-2px)';
    } else {
      if (isPrimary) {
        e.target.style.backgroundColor = theme.colors.primary;
      } else {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.color = theme.colors.primary;
      }
      e.target.style.transform = 'translateY(0)';
    }
  };

  return (
    <div>
      {/* Welcome Section */}
      <div style={styles.welcome}>
        <h1 style={styles.welcomeTitle}>
          {isNewUser 
            ? `>> Welcome to ByteBot, ${userName} <<`
            : `>> Welcome back, ${userName} <<`
          }
        </h1>
        <p style={styles.welcomeSubtitle}>
          {isNewUser 
            ? 'SYSTEM STATUS: READY FOR INITIALIZATION\nSet up your first chatbot and complete your profile to get started.'
            : 'SYSTEM STATUS: OPERATIONAL\nManage your AI chatbots, track conversations, and grow your business.'
          }
        </p>
        
        {isNewUser && (
          <div style={styles.newUserActions}>
            <button
              style={styles.actionButton}
              onMouseEnter={(e) => handleButtonHover(e, true, true)}
              onMouseLeave={(e) => handleButtonHover(e, false, true)}
              onClick={handleCreateAgent}
            >
              [CREATE FIRST BOT]
            </button>
            <button
              style={styles.secondaryButton}
              onMouseEnter={(e) => handleButtonHover(e, true, false)}
              onMouseLeave={(e) => handleButtonHover(e, false, false)}
              onClick={handleViewAccount}
            >
              [COMPLETE PROFILE]
            </button>
          </div>
        )}
      </div>

      {/* Stats Grid - Only show for existing users or simplified version for new users */}
      <div style={styles.statsGrid}>
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => handleStatCardHover(e, true)}
          onMouseLeave={(e) => handleStatCardHover(e, false)}
        >
          <div style={styles.statValue}>[{agents.length}]</div>
          <div style={styles.statLabel}>ACTIVE BOTS</div>
          <div style={styles.statSubtext}>
            {agents.length === 0 ? 'READY FOR DEPLOYMENT' : 'OPERATIONAL 24/7'}
          </div>
        </div>
        
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => handleStatCardHover(e, true)}
          onMouseLeave={(e) => handleStatCardHover(e, false)}
        >
          <div style={styles.statValue}>[{usageStats?.conversationCount || userProfile?.currentPeriodConversations || 0}]</div>
          <div style={styles.statLabel}>CONVERSATIONS</div>
          <div style={styles.statSubtext}>
            {billingInfo.cyclePeriod}<br/>
            RESETS IN {billingInfo.daysRemaining} DAYS
          </div>
        </div>
      </div>

      {/* Agent List or Create CTA */}
      {agents.length > 0 ? (
        <AgentList agents={agents} onManage={onEditAgent} />
      ) : null}
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      // Initialize user document if it doesn't exist
      initializeUserDocument(user);

      // Listen to user profile changes
      const userDocRef = doc(db, 'users', user.uid);
      const unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          setUserProfile(docSnapshot.data());
        }
      });

      // Listen to agents
      const q = query(
        collection(db, 'agents'),
        where('ownerId', '==', user.uid)
      );

      const unsubscribeAgents = onSnapshot(q, (snapshot) => {
        const agentList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAgents(agentList);
        setLoading(false);
      });

      return () => {
        unsubscribeUser();
        unsubscribeAgents();
      };
    }
  }, [user]);

  const initializeUserDocument = async (user) => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create user document with initial data
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          createdAt: new Date(),
          lastLoginAt: new Date(),
          subscriptionPlan: 'free',
          subscriptionStatus: 'active',
          agentIds: [],
          preferences: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            notifications: true,
            theme: 'retro-modern'
          },
          profile: {
            company: '',
            website: '',
            industry: '',
            teamSize: '1-5',
            useCase: ''
          }
        });
        console.log('User document created successfully');
      } else {
        // Update last login time
        await setDoc(userDocRef, {
          lastLoginAt: new Date()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error initializing user document:', error);
    }
  };

  // Load usage statistics
  useEffect(() => {
    if (user) {
      const loadUsageStats = async () => {
        try {
          const getUsageStats = httpsCallable(functions, 'getUsageStats');
          const result = await getUsageStats();
          setUsageStats(result.data);
        } catch (error) {
          console.error('Error loading usage stats:', error);
          setUsageStats({
            totalTokensUsed: 0,
            conversationCount: 0,
            conversations: {},
            currentMonth: new Date().toISOString().slice(0, 7),
          });
        }
      };
      loadUsageStats();
    }
  }, [user]);

  const handleEditAgent = (agent) => {
    navigate(`/dashboard/agent/${agent.id}`);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.typography.fontFamily,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: 0, // Match homepage - no padding on header itself
      borderBottom: `2px solid ${theme.colors.borderDark}`,
      position: 'sticky',
      top: 0,
      zIndex: 1001,
    },
    headerContent: {
      ...responsiveContainer,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '70px', // Match homepage navigation height exactly
      padding: `0 ${theme.spacing.lg}`, // Only left/right padding like homepage
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#fff',
      textDecoration: 'none',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.lg,
      fontSize: theme.typography.fontSizes.base,
      color: '#ccc',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoutButton: {
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      cursor: 'pointer',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.semibold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      transition: 'all 0.2s ease',
    },
    nav: {
      backgroundColor: theme.colors.surface,
      borderBottom: `2px solid ${theme.colors.borderDark}`,
      padding: 0,
      position: 'sticky',
      top: '70px',
      zIndex: 1000,
      boxShadow: theme.shadows.sm,
    },
    navContent: {
      ...responsiveContainer,
      display: 'flex',
      justifyContent: 'center',
      gap: 0,
    },
    navButton: {
      padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.semibold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      borderBottom: '3px solid transparent',
      transition: 'all 0.2s ease',
      color: theme.colors.text,
      minWidth: '140px',
      textDecoration: 'none',
      display: 'inline-block',
      textAlign: 'center',
    },
    navButtonActive: {
      borderBottomColor: theme.colors.primary,
      backgroundColor: `${theme.colors.primary}10`,
      color: theme.colors.primary,
    },
    content: {
      flex: 1,
      padding: `${theme.spacing['3xl']} 0`,
    },
    contentInner: {
      ...responsiveContainer,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh',
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    loadingText: {
      fontSize: theme.typography.fontSizes.xl,
      color: theme.colors.textLight,
      textAlign: 'center',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    loadingSpinner: {
      width: '50px',
      height: '50px',
      border: `4px solid ${theme.colors.border}`,
      borderTop: `4px solid ${theme.colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
  };

  const handleLogoutHover = (e, isHovering) => {
    if (isHovering) {
      e.target.style.backgroundColor = theme.colors.primary;
      e.target.style.color = '#fff';
    } else {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.color = theme.colors.primary;
    }
  };

  const navItems = [
    { key: '', label: '[MY BOTS]', path: '/dashboard' },
    { key: 'setup', label: '[CREATE NEW]', path: '/dashboard/setup' },
    { key: 'account', label: '[ACCOUNT]', path: '/dashboard/account' },
    { key: 'subscription', label: '[BILLING]', path: '/dashboard/subscription' },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color="#fff"
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </div>
          </div>
        </div>
        <div style={styles.content}>
          <div style={styles.contentInner}>
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner}></div>
              <div style={styles.loadingText}>
                [LOADING DASHBOARD...]
              </div>
            </div>
          </div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          {/* Logo - Goes to homepage, not dashboard home */}
          <Link to="/" style={styles.logo}>
            <div style={styles.logoIcon}>
              <ByteBotLogo 
                size={32} 
                color="#fff"
              />
            </div>
            [BYTE<span style={styles.logoAccent}>BOT</span>]
          </Link>
          <div style={styles.userInfo}>
            <span>USER: {user?.displayName || user?.email}</span>
            <button
              style={styles.logoutButton}
              onMouseEnter={(e) => handleLogoutHover(e, true)}
              onMouseLeave={(e) => handleLogoutHover(e, false)}
              onClick={onLogout}
            >
              [LOGOUT]
            </button>
          </div>
        </div>
      </div>

      {/* Navigation - Fixed/Sticky */}
      <div style={styles.nav}>
        <div style={styles.navContent}>
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              style={{
                ...styles.navButton,
                ...(isActivePath(item.path) ? styles.navButtonActive : {})
              }}
              onMouseEnter={(e) => {
                if (!isActivePath(item.path)) {
                  e.target.style.backgroundColor = `${theme.colors.primary}05`;
                  e.target.style.color = theme.colors.primary;
                  e.target.style.borderBottomColor = 'transparent'; // Keep border transparent
                }
              }}
              onMouseLeave={(e) => {
                if (!isActivePath(item.path)) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = theme.colors.text;
                  e.target.style.borderBottomColor = 'transparent'; // Ensure border stays transparent
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <div style={styles.contentInner}>
          <Suspense fallback={<LoadingSkeleton />}>
            <Routes>
              <Route 
                path="/" 
                element={
                  <DashboardHome 
                    user={user} 
                    agents={agents} 
                    usageStats={usageStats} 
                    userProfile={userProfile}
                    onEditAgent={handleEditAgent}
                  />
                } 
              />
              <Route 
                path="/setup" 
                element={<AgentSetup onComplete={() => navigate('/dashboard')} />} 
              />
              <Route 
                path="/account" 
                element={<AccountDetails user={user} />} 
              />
              <Route 
                path="/subscription" 
                element={<SubscriptionManager usageStats={usageStats} />} 
              />
              <Route 
                path="/agent/:agentId" 
                element={<AgentEditor onBack={() => navigate('/dashboard')} />} 
              />
            </Routes>
          </Suspense>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default Dashboard; 